import { NeedDto, NeedsActivityDto } from '@act/shared/data-transfer-objects';
import {
  Alert,
  Box,
  Button,
  ControlledCheckbox,
  ControlledDateField,
  ControlledSelect,
  ControlledTextField,
  FlexContainer,
  Masonry,
  Tile,
} from '@act/storybook';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import { Fragment, MutableRefObject } from 'react';
import {
  Control,
  FieldArrayWithId,
  FormState,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { NeedActionResourcesSection } from 'react-components/needs/need-action-resources/NeedActionResourcesSection';
import ResourceReferralForm from '../resourcing/ResourcereferralForm';
import ActivityLearnMoreContent from './ActivityLearnMoreContent';
import { ActionFormValues } from './useNeedActionFormtypes';
import { convertTimeStringToMinutes } from '../helpers/timeConverter.util';

export interface NeedActionFormBodyProps {
  need: NeedDto;
  overrideValues: Partial<ActionFormValues>;
  issueKey: string;
  activityOptions: NeedsActivityDto[];
  selectedActivity: NeedsActivityDto | null;
  control: Control<ActionFormValues>;
  formState: FormState<ActionFormValues>;
  setValue: UseFormSetValue<ActionFormValues>;
  watch: UseFormWatch<ActionFormValues>;
  getValues: UseFormGetValues<ActionFormValues>;
  minDate: DateTime;
  durationFocused: boolean;
  setDurationFocused: (focused: boolean) => void;
  formValues: ActionFormValues;
  resourceDetails: FieldArrayWithId<ActionFormValues, 'resourceDetails', 'key'>[];
  append: UseFieldArrayAppend<ActionFormValues, 'resourceDetails'>;
  remove: UseFieldArrayRemove;
  referralsCountRef: MutableRefObject<number | null>;
  placesAutocompleteEnabled: boolean;
  expandedForm: string | null;
  expandResourceForm: (key: string) => void;
  appendResourceDetails: () => void;
  confirmDelete: (index: number) => void;
  getTimeWindowMessage: () => string;
  openLearnMoreDialog: (config: {
    title: string;
    content: React.ReactNode;
  }) => void;
}

/**
 * Shared need-action form fields for drawer and dialog flows.
 *
 * @param props - Form control bindings and resource-referral handlers.
 * @returns Form field layout for need actions.
 */
export function NeedActionFormBody({
  need,
  overrideValues,
  issueKey,
  activityOptions,
  selectedActivity,
  control,
  formState,
  setValue,
  watch,
  getValues,
  minDate,
  durationFocused,
  setDurationFocused,
  formValues,
  resourceDetails,
  append,
  remove,
  referralsCountRef,
  placesAutocompleteEnabled,
  expandedForm,
  expandResourceForm,
  appendResourceDetails,
  confirmDelete,
  getTimeWindowMessage,
  openLearnMoreDialog,
}: NeedActionFormBodyProps) {
  const selectComponent =
    issueKey === 'issue_social-isolation' ? (
      <Masonry>
        {activityOptions.map(activity => (
          <Tile
            key={activity.id}
            iconName={activity.iconName}
            active={selectedActivity?.id === activity.id}
            onClick={() => setValue('activityId', activity.id)}
          >
            <h3>{activity.label}</h3>
            <p>{activity.overview}</p>
          </Tile>
        ))}
      </Masonry>
    ) : (
      <ControlledSelect
        control={control}
        errors={formState.errors}
        label="Activity*"
        name="activityId"
        required
        fullWidth
        options={activityOptions.map(activity => ({
          label: activity.label,
          value: activity.id,
        }))}
      />
    );

  return (
    <Box>
      {overrideValues.activityId === undefined && (
        <Fragment>
          <Box margin="22px 0 0">{selectComponent}</Box>
          <Box margin="22px 0 0">
            {!selectedActivity ? (
              <Alert
                severity="info"
                variant="tonal"
                trackingName="action-time-window-alert"
              >
                {getTimeWindowMessage()}
              </Alert>
            ) : (
              <Alert
                severity="info"
                variant="tonal"
                trackingName="action-time-window-alert"
                action={{
                  label: 'Learn more',
                  onClick: () =>
                    openLearnMoreDialog({
                      title: selectedActivity?.label,
                      content: (
                        <ActivityLearnMoreContent activity={selectedActivity} />
                      ),
                    }),
                }}
              >
                {getTimeWindowMessage()}
              </Alert>
            )}
          </Box>
        </Fragment>
      )}
      {overrideValues.date === undefined && (
        <Box margin="22px 0 0">
          <ControlledDateField
            control={control}
            label="Date*"
            name="date"
            disablePast={false}
            minDate={minDate}
            inputType="date"
            required
          />
        </Box>
      )}
      <Box margin="22px 0 0">
        <ControlledTextField
          control={control}
          name="duration"
          label="Duration*"
          iconAdornment={
            watch('duration') && !durationFocused ? <>minutes</> : ''
          }
          onFocusCallback={() => {
            setDurationFocused(true);
          }}
          onBlurCallback={(
            e: React.FocusEvent<
              HTMLInputElement | HTMLTextAreaElement,
              Element
            >,
            fn
          ) => {
            const value = e.target.value;
            setDurationFocused(false);
            const minutes = convertTimeStringToMinutes(value);
            fn(minutes);
          }}
          required
        />
      </Box>
      {overrideValues.description === undefined && (
        <Box margin="22px 0 0" width="100%">
          <ControlledTextField
            control={control}
            errors={formState.errors}
            name="description"
            label="Description*"
            required
            multiline
            minRows={4}
            maxRows={4}
            characterLimit={500}
          />
        </Box>
      )}
      <Box marginTop="16px">
        <ControlledCheckbox
          ariaLabel="Add resource referral checkbox"
          control={control}
          defaultChecked={false}
          label="Resource referred"
          name="resourceReferred"
          disabled={referralsCountRef.current > 0}
          onCheck={value => {
            if (resourceDetails.length === 0 && !!value) {
              appendResourceDetails();
            }
          }}
        />
      </Box>
      {formValues.resourceReferred ? (
        <Box margin="16px 0 24px">
          {placesAutocompleteEnabled ? (
            <NeedActionResourcesSection
              control={control}
              formState={formState}
              setValue={setValue}
              getValues={getValues}
              fields={resourceDetails}
              append={append}
              remove={remove}
              needIssueKey={issueKey}
            />
          ) : (
            resourceDetails?.map((field, index) => (
              <Box key={field?.key}>
                <ResourceReferralForm
                  control={control}
                  details={resourceDetails[index]}
                  formState={formState}
                  resourceDetailsIndex={index}
                  expanded={expandedForm === field.key}
                  expandResourceForm={() => expandResourceForm(field.key)}
                />
                <FlexContainer
                  direction="row"
                  height="auto"
                  overflowAllVisible
                  padding="16px 0"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Button
                    variant="contained"
                    onClick={appendResourceDetails}
                    startIcon={<AddIcon />}
                  >
                    Add another resource
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => confirmDelete(index)}
                    startIcon={<CloseIcon />}
                  >
                    Remove resource
                  </Button>
                </FlexContainer>
              </Box>
            ))
          )}
        </Box>
      ) : null}
      {overrideValues.markAsFulfilled === undefined && (
        <Box>
          <ControlledCheckbox
            ariaLabel="Mark as fulfilled checkbox"
            control={control}
            defaultChecked={!!need?.fulfilledAt || false}
            label="Need fulfilled"
            name="markAsFulfilled"
            disabled={!!need?.fulfilledAt}
          />
        </Box>
      )}
    </Box>
  );
}
